import React, { FC, isValidElement, memo } from 'react';
import { styled } from '../../styles';
import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/types';
import { type TokenColor, Typography } from '../Typography/Typography';
import { BpDesignSystem } from '../../types';
import * as tokens from '@utmost.co/utmost-design-tokens';
import { Grid } from '../Grid/Grid';
import { Button, ButtonProps } from '../Button/Button';

type InfoType = 'ALERT' | 'ERROR' | 'INFO' | 'SUCCESS';

export type InfoBoxProps = BpDesignSystem.PropsWithChildren<{
  maxWidth?: string;
  title?: string | React.ReactNode;
  type: InfoType;
  actions?: React.ReactNode | ButtonProps;
  icon?: IconName;
  breakWords?: boolean;
  iconAlign?: 'center' | 'flex-start';
}>;

/**
 * designs: https://www.figma.com/design/xxyJesUT4jFpgjGmqA61Qj/Engagements?node-id=2397-148445&t=piTNd1kds4YX4giC-0
 */
export const InfoBox: FC<InfoBoxProps> = memo(
  ({ type, icon, title, maxWidth, dataTest, children, actions, breakWords = false, iconAlign = 'center', ...rest }) => {
    const details = getTypeDetails(type, icon);
    const textColor = tokens.colorOnSurface;

    return (
      <StyledContainer
        data-test={dataTest}
        $maxWidth={maxWidth}
        $backgroundColor={details?.backgroundColor}
        $accentColor={details?.accentColor}
        {...rest}
      >
        <Grid container spacing={5} size={{ xs: 12 }} alignItems={iconAlign}>
          <Grid size="auto">
            <Icon name={details?.iconName || 'info'} color={details?.accentColor} />
          </Grid>
          <Grid size="grow">
            <StyledContent $breakWords={breakWords}>
              {!!title && (
                <Typography variant="body" large={!!children} color={!!children ? details?.titleColor : textColor}>
                  {title}
                </Typography>
              )}
              {!!children && (
                <Typography variant="body" color={textColor}>
                  {children}
                </Typography>
              )}
            </StyledContent>
          </Grid>
          {!!actions && (
            <Grid size="auto">
              {isReactNode(actions) ? actions : <Button color="primary" children="Dismiss" {...actions} />}
            </Grid>
          )}
        </Grid>
      </StyledContainer>
    );
  }
);

function getTypeDetails(
  type: InfoType,
  iconName?: IconName
):
  | { backgroundColor: string; titleColor: `#${string}` | TokenColor; accentColor: TokenColor; iconName: IconName }
  | undefined {
  switch (type) {
    case 'ALERT':
      return {
        backgroundColor: tokens.colorWarningContainer,
        titleColor: 'colorOnWarningContainer',
        accentColor: 'paletteAmber70',
        iconName: iconName ?? 'warning'
      };
    case 'INFO':
      return {
        backgroundColor: tokens.colorPrimaryContainer,
        titleColor: 'colorOnPrimaryContainer',
        accentColor: 'colorPrimary',
        iconName: iconName ?? 'info'
      };
    case 'ERROR':
      return {
        backgroundColor: tokens.colorAlertContainer,
        titleColor: 'colorOnAlertContainer',
        accentColor: 'colorAlert',
        iconName: iconName ?? 'error'
      };
    case 'SUCCESS':
      return {
        backgroundColor: tokens.colorSuccessContainer,
        titleColor: 'colorOnSuccessContainer',
        accentColor: 'colorSuccess',
        iconName: iconName ?? 'info'
      };
  }
}

const StyledContainer = styled('div')<{
  $maxWidth?: string;
  $backgroundColor: string | undefined;
  $accentColor: TokenColor | undefined;
}>(({ theme, $accentColor, $backgroundColor, $maxWidth }) => ({
  padding: theme.spacingSize.ml,
  borderRadius: theme.tokens.radiusDefault,
  maxWidth: $maxWidth,
  backgroundColor: $backgroundColor,
  borderLeft: `4px solid ${$accentColor ? theme.tokens[$accentColor] : undefined}`
}));

const StyledContent = styled('div')<{ $breakWords: boolean }>`
  > .body1 {
    text-wrap: pretty;
    word-break: ${({ $breakWords }) => ($breakWords ? 'break-all' : 'normal')};
  }  
  > .h4 {
    text-wrap: balance;
  }
`;

function isReactNode(input: React.ReactNode | ButtonProps): input is React.ReactNode {
  return isValidElement(input);
}
